import { createContext, useContext, useState, useEffect } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
          const response = await api.get("/auth/me");
          setUser(response.data);
        } catch (error) {
          console.error("Auth init failed:", error);
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  const login = async (identifier, password) => {
    const response = await api.post("/auth/login", { identifier, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    api.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    delete api.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
  };

  const acceptNDA = async () => {
    await api.post("/auth/accept-nda", { accepted: true });
    setUser(prev => ({ ...prev, nda_accepted: true }));
  };

  const isSuperAdmin = () => {
    return user && user.role === "super_admin";
  };

  const isAdmin = () => {
    return user && ["super_admin", "sales_manager", "production_manager", "accounts"].includes(user.role);
  };

  const isCustomer = () => {
    return user && ["customer_admin", "customer_sub_user"].includes(user.role);
  };

  const hasRole = (...roles) => {
    return user && roles.includes(user.role);
  };

  return (
    <AuthContext.Provider value={{ 
      user, token, loading, login, logout, acceptNDA, 
      isSuperAdmin, isAdmin, isCustomer, hasRole 
    }}>
      {children}
    </AuthContext.Provider>
  );
};
